import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Upload, Trash2, Save, Plus, X, ImageIcon } from "lucide-react";
import { API, formatApiError } from "@/contexts/AuthContext";

// --------------------------------------------------------------------
// Phase 2C · Brand Kit v1 — additive Settings panel.
// Consumes GET/PATCH /api/brand-kit.
// Uploads reuse the LOCKED Phase 2B POST /api/files with the four brand
// kinds (logo | logo_mono | favicon | brand_asset).
// --------------------------------------------------------------------

const FONT_WHITELIST = [
  "Inter", "Roboto", "Poppins", "Playfair Display", "Merriweather", "Lora",
  "Manrope", "Space Grotesk", "IBM Plex Sans", "IBM Plex Serif",
  "DM Sans", "DM Serif Display", "Source Sans 3", "Source Serif 4",
  "Nunito", "Work Sans",
];
const TONE_ENUM = ["professional", "friendly", "playful", "authoritative", "warm", "technical"];
const READING_LEVEL_ENUM = ["general", "expert", "executive"];
const BRAND_ASSET_CAP = 12;

const KIND_ACCEPT = {
  logo:        "image/png,image/jpeg,image/webp",
  logo_mono:   "image/png,image/jpeg,image/webp",
  favicon:     "image/png,image/x-icon,image/vnd.microsoft.icon",
  brand_asset: "image/png,image/jpeg,image/webp",
};

export default function BrandKitPanel() {
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingColors, setSavingColors] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const [savingTagline, setSavingTagline] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);

  const [colors, setColors] = useState({ primary: "", secondary: "", accent: "" });
  const [type, setType] = useState({ heading_font: "", body_font: "" });
  const [tagline, setTagline] = useState("");
  const [voice, setVoice] = useState({ tone: "", reading_level: "", do: [], dont: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/brand-kit`);
      setKit(data);
      setColors({
        primary:   data.colors?.primary   || "",
        secondary: data.colors?.secondary || "",
        accent:    data.colors?.accent    || "",
      });
      setType({
        heading_font: data.typography?.heading_font || "",
        body_font:    data.typography?.body_font    || "",
      });
      setTagline(data.tagline || "");
      setVoice({
        tone:          data.voice?.tone          || "",
        reading_level: data.voice?.reading_level || "",
        do:            [...(data.voice?.do   || [])],
        dont:          [...(data.voice?.dont || [])],
      });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load Brand Kit.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !kit) {
    return (
      <section className="mt-6 bg-white border border-[#eee] rounded-2xl p-6" data-testid="brand-kit-panel-loading">
        <div className="flex items-center gap-2 text-[13.5px] text-[#666]">
          <Loader2 size={14} className="animate-spin" /> Loading Brand Kit…
        </div>
      </section>
    );
  }

  return (
    <div data-testid="brand-kit-panel" className="space-y-6">
      <div className="mt-6">
        <h2 className="text-[18px] font-semibold">Brand Kit</h2>
        <p className="text-[13.5px] text-[#555] mt-1">
          Your workspace-level brand identity. Assets you save here become the canonical source
          new modules will read from — your current invoice logo and other legacy branding are unchanged.
        </p>
      </div>

      <BrandMarksCard kit={kit} onChange={load} />

      <ColorsCard
        colors={colors}
        setColors={setColors}
        saving={savingColors}
        onSave={async () => {
          setSavingColors(true);
          try {
            await axios.patch(`${API}/brand-kit`, { colors });
            // Regression repair · Issue 7: PATCH response shape differs
            // from GET shape, so refetch via load() (same pattern as the
            // working BrandAssetsCard flow) instead of setKit(data).
            await load();
            toast.success("Brand colors saved.");
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Could not save colors.");
          } finally {
            setSavingColors(false);
          }
        }}
      />

      <TypographyCard
        type={type}
        setType={setType}
        saving={savingType}
        onSave={async () => {
          setSavingType(true);
          try {
            await axios.patch(`${API}/brand-kit`, { typography: type });
            await load();
            toast.success("Typography saved.");
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Could not save typography.");
          } finally {
            setSavingType(false);
          }
        }}
      />

      <TaglineCard
        tagline={tagline}
        setTagline={setTagline}
        saving={savingTagline}
        onSave={async () => {
          setSavingTagline(true);
          try {
            await axios.patch(`${API}/brand-kit`, { tagline });
            await load();
            toast.success("Tagline saved.");
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Could not save tagline.");
          } finally {
            setSavingTagline(false);
          }
        }}
      />

      <VoiceCard
        voice={voice}
        setVoice={setVoice}
        saving={savingVoice}
        onSave={async () => {
          setSavingVoice(true);
          try {
            await axios.patch(`${API}/brand-kit`, { voice });
            await load();
            toast.success("Brand voice saved.");
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Could not save voice.");
          } finally {
            setSavingVoice(false);
          }
        }}
      />

      <BrandAssetsCard kit={kit} onChange={load} />
    </div>
  );
}

/* ==================================================================== */
/* Card · Brand marks (logo, mono logo, favicon)                        */
/* ==================================================================== */
function BrandMarksCard({ kit, onChange }) {
  return (
    <section
      className="bg-white border border-[#eee] rounded-2xl p-6"
      data-testid="brand-kit-marks-card"
    >
      <h3 className="text-[15px] font-semibold mb-4">Brand marks</h3>
      <p className="text-[13px] text-[#666] mb-5">
        Files are stored in your workspace file library and referenced here. Removing a slot
        below does not delete the file from your library.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <MarkSlot
          label="Primary logo"
          kind="logo"
          slotKey="logo_file_id"
          testid="brand-kit-logo"
          slot={kit.logo}
          onChange={onChange}
        />
        <MarkSlot
          label="Monochrome logo"
          kind="logo_mono"
          slotKey="logo_mono_file_id"
          testid="brand-kit-logo-mono"
          slot={kit.logo_mono}
          onChange={onChange}
        />
        <MarkSlot
          label="Favicon"
          kind="favicon"
          slotKey="favicon_file_id"
          testid="brand-kit-favicon"
          slot={kit.favicon}
          onChange={onChange}
        />
      </div>
    </section>
  );
}

function MarkSlot({ label, kind, slotKey, testid, slot, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data: uploaded } = await axios.post(`${API}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await axios.patch(`${API}/brand-kit`, { [slotKey]: uploaded.id });
      toast.success(`${label} saved.`);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/brand-kit`, { [slotKey]: null });
      toast.success(`${label} removed.`);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not remove.");
    } finally {
      setBusy(false);
    }
  };

  const url = slot?.available && slot?.download_url ? `${API.replace(/\/api$/, "")}${slot.download_url}` : null;

  return (
    <div className="border border-[#eee] rounded-xl p-3" data-testid={`${testid}-slot`}>
      <div className="text-[12.5px] font-medium mb-2">{label}</div>
      <div
        className="w-full aspect-square rounded-lg border border-dashed flex items-center justify-center overflow-hidden bg-[#FAFAFB]"
        style={{ borderColor: "#ddd" }}
        data-testid={`${testid}-preview`}
      >
        {url ? (
          <img src={url} alt={label} className="max-w-full max-h-full object-contain" />
        ) : (
          <ImageIcon size={26} className="text-[#aaa]" />
        )}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="zy-btn-primary text-[12.5px] disabled:opacity-70"
          data-testid={`${testid}-upload`}
        >
          {busy ? <><Loader2 size={12} className="animate-spin" /> Working…</> : <><Upload size={12} /> Upload</>}
        </button>
        {slot?.file_id && (
          <button
            type="button"
            onClick={clear}
            disabled={busy}
            className="zy-btn-outline text-[12.5px]"
            data-testid={`${testid}-remove`}
          >
            <Trash2 size={12} /> Remove
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={KIND_ACCEPT[kind]}
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid={`${testid}-input`}
        />
      </div>
      {slot?.file_id && !slot?.available && (
        <p className="text-[11.5px] text-[#a15c00] mt-2">
          Referenced file is not available (deleted or unavailable).
        </p>
      )}
    </div>
  );
}

/* ==================================================================== */
/* Card · Colors                                                        */
/* ==================================================================== */
function ColorsCard({ colors, setColors, saving, onSave }) {
  const swatch = (key, testid) => (
    <div className="flex items-center gap-3" data-testid={`brand-kit-color-${key}-row`}>
      <div
        className="w-10 h-10 rounded-md border"
        style={{ background: colors[key] || "#f4f4f5", borderColor: "#e6e6e6" }}
        data-testid={`brand-kit-color-${key}-swatch`}
      />
      <Input
        value={colors[key]}
        placeholder="#RRGGBB"
        onChange={(e) => setColors({ ...colors, [key]: e.target.value })}
        className="uppercase font-mono text-[13px] max-w-[140px]"
        data-testid={`brand-kit-color-${key}`}
      />
      <Label className="text-[12.5px] text-[#666] capitalize">{key}</Label>
    </div>
  );
  return (
    <section
      className="bg-white border border-[#eee] rounded-2xl p-6"
      data-testid="brand-kit-colors-card"
    >
      <h3 className="text-[15px] font-semibold mb-4">Colors</h3>
      <p className="text-[13px] text-[#666] mb-4">
        Hex only, format <code>#RRGGBB</code>. Leave a field empty to unset it.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {swatch("primary",   "primary")}
        {swatch("secondary", "secondary")}
        {swatch("accent",    "accent")}
      </div>
      <div className="pt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="zy-btn-primary disabled:opacity-70"
          data-testid="brand-kit-colors-save"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save colors</>}
        </button>
      </div>
    </section>
  );
}

/* ==================================================================== */
/* Card · Typography                                                    */
/* ==================================================================== */
function TypographyCard({ type, setType, saving, onSave }) {
  const dropdown = (key, testid, label) => (
    <div className="space-y-1.5">
      <Label className="text-[12.5px] font-medium">{label}</Label>
      <select
        value={type[key]}
        onChange={(e) => setType({ ...type, [key]: e.target.value })}
        className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white"
        data-testid={`brand-kit-font-${testid}`}
      >
        <option value="">— None —</option>
        {FONT_WHITELIST.map((f) => (
          <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
        ))}
      </select>
      {type[key] && (
        <p
          className="text-[16px] mt-2 text-[#333]"
          style={{ fontFamily: `'${type[key]}', system-ui` }}
          data-testid={`brand-kit-font-${testid}-preview`}
        >
          The quick brown fox jumps over the lazy dog.
        </p>
      )}
    </div>
  );
  return (
    <section
      className="bg-white border border-[#eee] rounded-2xl p-6"
      data-testid="brand-kit-typography-card"
    >
      <h3 className="text-[15px] font-semibold mb-4">Typography</h3>
      <p className="text-[13px] text-[#666] mb-4">
        We store only the family identifier. Font files are loaded from Google Fonts on demand at render time.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {dropdown("heading_font", "heading", "Heading font")}
        {dropdown("body_font",    "body",    "Body font")}
      </div>
      <div className="pt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="zy-btn-primary disabled:opacity-70"
          data-testid="brand-kit-typography-save"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save typography</>}
        </button>
      </div>
    </section>
  );
}

/* ==================================================================== */
/* Card · Tagline                                                       */
/* ==================================================================== */
function TaglineCard({ tagline, setTagline, saving, onSave }) {
  return (
    <section
      className="bg-white border border-[#eee] rounded-2xl p-6"
      data-testid="brand-kit-tagline-card"
    >
      <h3 className="text-[15px] font-semibold mb-4">Tagline</h3>
      <Input
        value={tagline}
        onChange={(e) => setTagline(e.target.value.slice(0, 140))}
        placeholder="One line that captures what you do."
        data-testid="brand-kit-tagline"
      />
      <div className="text-[11.5px] text-[#888] mt-1.5 text-right">{tagline.length}/140</div>
      <div className="pt-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="zy-btn-primary disabled:opacity-70"
          data-testid="brand-kit-tagline-save"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save tagline</>}
        </button>
      </div>
    </section>
  );
}

/* ==================================================================== */
/* Card · Brand voice                                                   */
/* ==================================================================== */
function VoiceCard({ voice, setVoice, saving, onSave }) {
  const addItem = (key) => {
    if (voice[key].length >= 8) return;
    setVoice({ ...voice, [key]: [...voice[key], ""] });
  };
  const updItem = (key, i, v) => {
    const next = [...voice[key]];
    next[i] = v.slice(0, 120);
    setVoice({ ...voice, [key]: next });
  };
  const rmItem = (key, i) => {
    const next = [...voice[key]];
    next.splice(i, 1);
    setVoice({ ...voice, [key]: next });
  };

  const chipEditor = (key, label, testidKey) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[12.5px] font-medium">{label}</Label>
        <button
          type="button"
          onClick={() => addItem(key)}
          disabled={voice[key].length >= 8}
          className="text-[12px] text-[#0a58ff] disabled:text-[#aaa] flex items-center gap-1"
          data-testid={`brand-kit-voice-${testidKey}-add`}
        >
          <Plus size={12} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {voice[key].length === 0 && (
          <div className="text-[12.5px] text-[#888] italic">No items yet.</div>
        )}
        {voice[key].map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={v}
              onChange={(e) => updItem(key, i, e.target.value)}
              placeholder={key === "do" ? "Do write in short, warm sentences" : "Don't use jargon"}
              data-testid={`brand-kit-voice-${testidKey}-item-${i}`}
            />
            <button
              type="button"
              onClick={() => rmItem(key, i)}
              className="text-[#888] hover:text-[#c00]"
              data-testid={`brand-kit-voice-${testidKey}-remove-${i}`}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      className="bg-white border border-[#eee] rounded-2xl p-6"
      data-testid="brand-kit-voice-card"
    >
      <h3 className="text-[15px] font-semibold mb-4">Brand voice</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-[12.5px] font-medium">Tone</Label>
          <select
            value={voice.tone}
            onChange={(e) => setVoice({ ...voice, tone: e.target.value })}
            className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white capitalize"
            data-testid="brand-kit-voice-tone"
          >
            <option value="">— None —</option>
            {TONE_ENUM.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12.5px] font-medium">Reading level</Label>
          <select
            value={voice.reading_level}
            onChange={(e) => setVoice({ ...voice, reading_level: e.target.value })}
            className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white capitalize"
            data-testid="brand-kit-voice-reading-level"
          >
            <option value="">— None —</option>
            {READING_LEVEL_ENUM.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5">
        {chipEditor("do",   "Do write like this",   "do")}
        {chipEditor("dont", "Don't write like this", "dont")}
      </div>
      <div className="pt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="zy-btn-primary disabled:opacity-70"
          data-testid="brand-kit-voice-save"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save voice</>}
        </button>
      </div>
    </section>
  );
}

/* ==================================================================== */
/* Card · Brand assets grid                                             */
/* ==================================================================== */
function BrandAssetsCard({ kit, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const assets = kit.brand_assets || [];
  const remaining = BRAND_ASSET_CAP - assets.length;

  const uploadOne = async (file) => {
    if (!file) return;
    if (remaining <= 0) {
      toast.error(`Maximum ${BRAND_ASSET_CAP} brand assets reached.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", "brand_asset");
      fd.append("file", file);
      const { data: up } = await axios.post(`${API}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ids = assets.map((a) => a.file_id).filter(Boolean);
      await axios.patch(`${API}/brand-kit`, { brand_asset_file_ids: [...ids, up.id] });
      toast.success("Brand asset added.");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAsset = async (fid) => {
    setBusy(true);
    try {
      const ids = assets.map((a) => a.file_id).filter((x) => x && x !== fid);
      await axios.patch(`${API}/brand-kit`, { brand_asset_file_ids: ids });
      toast.success("Brand asset removed.");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not remove.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-white border border-[#eee] rounded-2xl p-6"
      data-testid="brand-kit-assets-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[15px] font-semibold">Brand assets</h3>
          <p className="text-[13px] text-[#666] mt-1">
            Extra images that belong to your brand (patterns, textures, product marks). Up to {BRAND_ASSET_CAP}.
          </p>
        </div>
        <div className="text-[12.5px] text-[#888]">{assets.length}/{BRAND_ASSET_CAP}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {assets.map((a, i) => {
          const url = a.available && a.download_url
            ? `${API.replace(/\/api$/, "")}${a.download_url}` : null;
          return (
            <div key={a.file_id || i} className="relative group border border-[#eee] rounded-lg overflow-hidden bg-[#FAFAFB]" data-testid={`brand-kit-asset-${i}`}>
              <div className="aspect-square flex items-center justify-center">
                {url ? (
                  <img src={url} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageIcon size={22} className="text-[#aaa]" />
                )}
              </div>
              <button
                type="button"
                onClick={() => removeAsset(a.file_id)}
                disabled={busy}
                className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition"
                data-testid={`brand-kit-asset-remove-${i}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          );
        })}
        {remaining > 0 && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="border border-dashed border-[#ccc] rounded-lg aspect-square flex flex-col items-center justify-center text-[#666] hover:bg-[#FAFAFB] disabled:opacity-70"
            data-testid="brand-kit-asset-upload"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            <span className="text-[11px] mt-1">Add</span>
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={KIND_ACCEPT.brand_asset}
        onChange={(e) => uploadOne(e.target.files?.[0])}
        className="hidden"
        data-testid="brand-kit-asset-input"
      />
    </section>
  );
}
